import React, { useMemo, useState } from 'react'
import Moment from 'moment-timezone'
import { FaTrash, FaTrashAlt, FaChevronDown, FaChevronUp } from 'react-icons/fa'
import { BiPencil } from 'react-icons/bi'

import { FlexContainer, YSpacing } from '@components/common'
import { Checkbox } from '@components/common/form'
import { TooltipModal } from '@components/common/modal'
import {
  EditDateMenuCutoffModal,
  GroupOrderCancellationModal,
} from '@components/groupOrders'
import { AuthorizedDisplay } from '@containers/common/auth'

import { moneyString, hasAnyMenuItemMods, hasMenuItemMods } from '@utils'
import {
  getSortedModGroups,
  getSortedMods,
  getVendorMenuGroups,
} from '@utils/groupOrderUtils'

import {
  GroupOrder,
  ClientGlobalSettings,
  DateMenusDraft,
  DateMenus,
  MenuItem,
  Menu,
  AvailableMenuItemModGroup,
  ShowInfoModalReq,
  CancellationReq,
  CancellationPreviewResp,
  CancelledObjectType,
  CancellationResp,
  CancelGroupOrderObjectReq,
  CancellationScope,
  CancelledObject,
} from '@types'

type VendorMenuGroup = {
  vendorId: string
  vendorName: string
  menuCount: number
  totalOrderCount: number
  menus: {
    id: string
    menuName: string
    orderCount: number
    cutoffTime: string
    entrees: MenuItem[]
    sides: MenuItem[]
    drinks: MenuItem[]
    desserts: MenuItem[]
    active: boolean
    lastActive: string | undefined
    budgetPerPerson: number
    isOverBudget: boolean
    cutoffEmailSentAt: string | null
    cancellationEventId: string | null
  }[]
  cancelled: boolean
}

interface ChefMenusProps {
  groupOrder: GroupOrder | null
  clientGlobalSettings: ClientGlobalSettings | null
  dateMenu: Partial<DateMenusDraft | DateMenus>
  orderCountMap: Record<string, number>
  updatedCutoffMap: Record<string, boolean>
  editCutoffMenu: Menu | undefined
  showEditCutoffTimeModal: boolean
  getBudgetPerPersonValue: () => number | null
  syncMenu: (menu: Menu) => Promise<void>
  reloadDateMenu: () => Promise<void>
  onRemoveMenus: (menuIds: string[]) => void
  onChangeMenuActive: (
    menuId: string,
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void
  onEditMenuCutoffTime: (editCutoffMenu: Menu) => void
  hideEditCutoffTimeModal: () => void
  displaySuccessMessage: (message: string) => Promise<void>
  displayFailureMessage: (message: string) => Promise<void>
  displayWarningMessage: (message: string) => Promise<void>
  showConfirmationModal: (obj: { text: string }) => Promise<boolean>
  saveCutoffMenu: (menu: Menu) => void
  getCancelPreview: (
    grpId: string,
    req: CancellationReq,
  ) => Promise<CancellationPreviewResp | null>
  cancelGroupOrderObject: (
    grpId: string,
    req: CancelGroupOrderObjectReq,
  ) => Promise<CancellationResp | null>
  showInfoModal: (req: ShowInfoModalReq) => void
  closeInfoModal: () => void
}

const isBeforeCutoff = (cutoffTime: string) => {
  return Moment().isBefore(Moment(cutoffTime))
}

const ChefMenus: React.FC<ChefMenusProps> = ({
  groupOrder,
  clientGlobalSettings,
  dateMenu,
  orderCountMap,
  updatedCutoffMap,
  editCutoffMenu,
  showEditCutoffTimeModal,
  getBudgetPerPersonValue,
  syncMenu,
  reloadDateMenu,
  onRemoveMenus,
  onChangeMenuActive,
  onEditMenuCutoffTime: editMenuCutoffTime,
  hideEditCutoffTimeModal,
  displaySuccessMessage,
  displayFailureMessage,
  displayWarningMessage,
  showConfirmationModal,
  saveCutoffMenu,
  getCancelPreview,
  cancelGroupOrderObject,
  showInfoModal,
  closeInfoModal,
}) => {
  const menusByVendor = useMemo(() => {
    return getVendorMenuGroups(dateMenu, orderCountMap, getBudgetPerPersonValue)
  }, [dateMenu, orderCountMap, getBudgetPerPersonValue])

  const formattedDateMenuCutoffTime = useMemo(() => {
    if (dateMenu && dateMenu.cutoffTime) {
      const { cutoffTime } = dateMenu

      return typeof cutoffTime === 'string' ? cutoffTime : cutoffTime.toJSON()
    }

    return ''
  }, [dateMenu])

  const onEditMenuCutoffTime = (menuId: string) => {
    const menu = dateMenu?.menus?.find((menu) => menu.id === menuId)
    if (menu) {
      editMenuCutoffTime(menu)
    }
  }

  const getCancellationTimingStatus = (
    menu: VendorMenuGroup['menus'][number],
  ) => {
    if (dateMenu && Moment(dateMenu.date).isBefore(Moment())) {
      return 'Post Delivery of Delivery Date'
    }

    if (Moment(menu.cutoffTime).isAfter(Moment())) {
      return 'Pre Menu Cutoff'
    }

    return 'Post Menu Cutoff'
  }

  const onLoadCancelPreview = async (req: CancellationReq) => {
    if (!groupOrder?.id) {
      return null
    }
    const preview = await getCancelPreview(groupOrder.id, req)

    return preview
  }

  const onCancelGroupOrderObject = async (req: CancelGroupOrderObjectReq) => {
    if (!groupOrder?.id) {
      return null
    }
    const resp = await cancelGroupOrderObject(groupOrder.id, req)

    return resp
  }

  const onShowCancelModal = (
    title: string,
    cancelledObject: CancelledObject,
    cancellationTimingStatus: string,
  ) => {
    if (!groupOrder) {
      return
    }

    showInfoModal({
      title,
      content: (
        <GroupOrderCancellationModal
          groupOrder={groupOrder}
          clientGlobalSettings={clientGlobalSettings}
          close={closeInfoModal}
          cancelledObject={cancelledObject}
          cancellationTimingStatus={cancellationTimingStatus}
          loadCancelPreview={onLoadCancelPreview}
          cancelGroupOrderObject={onCancelGroupOrderObject}
          reloadDateMenu={reloadDateMenu}
          flashSuccessMessage={displaySuccessMessage}
          flashFailureMessage={displayFailureMessage}
        />
      ),
      width: '90vw',
      height: '90vh',
      noHeader: true,
    })
  }

  const onDeleteMenu = (
    menu: VendorMenuGroup['menus'][number],
    vendor: VendorMenuGroup,
  ) => {
    if (!groupOrder) {
      onRemoveMenus([menu.id])
    } else {
      onShowCancelModal(
        'Delete Menu',
        {
          grpId: groupOrder.id,
          id: menu.id,
          name: menu.menuName,
          from: vendor.vendorName,
          type: CancelledObjectType.MENU,
          scope: CancellationScope.MENU,
        },
        getCancellationTimingStatus(menu),
      )
    }
  }

  const onDeleteVendorMenus = (vendor: VendorMenuGroup) => {
    if (!groupOrder) {
      onRemoveMenus(vendor.menus.map((menu) => menu.id))
    } else {
      onShowCancelModal(
        'Delete Vendor Menus',
        {
          grpId: groupOrder.id,
          id: vendor.vendorId,
          name: vendor.vendorName,
          type: CancelledObjectType.VENDOR,
          scope: CancellationScope.VENDOR,
        },
        getCancellationTimingStatus(vendor.menus[0]),
      )
    }
  }

  const onDeleteMenuItem = (
    item: MenuItem,
    menu: VendorMenuGroup['menus'][number],
  ) => {
    groupOrder &&
      onShowCancelModal(
        'Delete Menu Item',
        {
          grpId: groupOrder.id,
          id: item.id,
          name: item.name,
          from: menu.menuName,
          type: CancelledObjectType.ITEM,
          scope: CancellationScope.ITEM,
        },
        getCancellationTimingStatus(menu),
      )
  }

  if (!menusByVendor || Object.keys(menusByVendor).length === 0) {
    return (
      <div>
        <p>No menus selected for this day</p>
        <YSpacing height="10px" />
      </div>
    )
  }

  return (
    <>
      {showEditCutoffTimeModal && editCutoffMenu && (
        <EditDateMenuCutoffModal
          editMenu={editCutoffMenu}
          dateMenuCutoffTime={formattedDateMenuCutoffTime}
          displayFailureMessage={displayFailureMessage}
          displayWarningMessage={displayWarningMessage}
          hideModal={hideEditCutoffTimeModal}
          saveMenu={saveCutoffMenu}
          showConfirmationModal={showConfirmationModal}
        />
      )}
      {Object.entries(menusByVendor)
        .sort((vendorA, vendorB) =>
          vendorA[1].vendorName.localeCompare(vendorB[1].vendorName),
        )
        .map(([vendorId, vendor]) => (
          <ChefMenuGroup
            key={vendorId}
            dateMenu={dateMenu}
            groupOrder={groupOrder}
            vendorMenus={vendor}
            orderCountMap={orderCountMap}
            updatedCutoffMap={updatedCutoffMap}
            syncMenu={syncMenu}
            onChangeMenuActive={onChangeMenuActive}
            onEditMenuCutoffTime={onEditMenuCutoffTime}
            onDeleteMenuItem={onDeleteMenuItem}
            onDeleteMenu={onDeleteMenu}
            onDeleteVendorMenus={onDeleteVendorMenus}
          />
        ))}
    </>
  )
}

export default ChefMenus

interface ChefMenuGroupProps {
  groupOrder: GroupOrder | null
  dateMenu: Partial<DateMenusDraft | DateMenus>
  vendorMenus: VendorMenuGroup
  orderCountMap: Record<string, number>
  updatedCutoffMap: Record<string, boolean>
  syncMenu: (menu: Menu) => Promise<void>
  onChangeMenuActive: (
    menuId: string,
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void
  onEditMenuCutoffTime: (menuId: string) => void
  onDeleteMenuItem: (
    item: MenuItem,
    menu: VendorMenuGroup['menus'][number],
  ) => void
  onDeleteMenu: (
    menu: VendorMenuGroup['menus'][number],
    vendor: VendorMenuGroup,
  ) => void
  onDeleteVendorMenus: (vendor: VendorMenuGroup) => void
}
const ChefMenuGroup: React.FC<ChefMenuGroupProps> = ({
  groupOrder,
  dateMenu,
  vendorMenus,
  orderCountMap,
  updatedCutoffMap,
  syncMenu,
  onChangeMenuActive,
  onEditMenuCutoffTime,
  onDeleteMenuItem,
  onDeleteMenu,
  onDeleteVendorMenus,
}) => {
  const [expandedMenuIds, setExpandedMenuIds] = useState<
    Record<string, boolean>
  >({})
  const [showItemMods, setShowItemMods] = useState<Record<string, boolean>>({})
  const earliestMenuCutoffTime = useMemo(() => {
    return vendorMenus.menus.sort((a, b) =>
      Moment(a.cutoffTime).diff(Moment(b.cutoffTime)),
    )[0].cutoffTime
  }, [vendorMenus.menus])

  const renderItemCategory = (
    label: string,
    menu: VendorMenuGroup['menus'][number],
    items: MenuItem[],
    menuCutoffTime: string,
    isMenuCancelled: boolean,
  ) => {
    if (items.length === 0) {
      return null
    }

    const getItemRating = (item: MenuItem) => {
      const { averageRating } = item.ratingInfo || {}
      if (averageRating) {
        return `${averageRating.toFixed(2)}⭐`
      }

      return 'N/A'
    }

    const renderModGroup = (modGroup: AvailableMenuItemModGroup) => {
      return (
        <div
          key={modGroup.id}
          className="flex flex-col p-2 mb-3"
          style={{ borderTop: '1px solid #cbd5e0' }}
        >
          <div className="flex flex-row items-center">
            <div className="uppercase mr-3" style={{ fontStyle: 'italic' }}>
              {modGroup.verb}
            </div>
            <div>{modGroup.name}</div>
          </div>
          <div className="flex flex-col mt-2">
            {getSortedMods(modGroup.availableMods).map((mod) => (
              <div
                className="flex flex-row items-center justify-between"
                key={mod.id}
              >
                <div className="mr-4">{mod.name}</div>
                <div>${moneyString(mod.clientPrice)}</div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    const renderMenuItem = (item: MenuItem) => {
      const isCancelled = isMenuCancelled || item.cancellationEventId !== null

      const backgroundColor = isCancelled ? 'bg-red-200' : 'bg-gray-200'
      const borderColor = isCancelled ? '#f56565' : '#e2e8f0'

      return (
        <div
          key={item.id}
          className={`flex flex-row items-center p-2 rounded-md justify-between mb-2 ${backgroundColor}`}
          style={{ border: `1px solid ${borderColor}` }}
        >
          <div className="flex flex-col">
            <p className="text-md font-semibold">{item.name}</p>
            <div className="flex flex-row items-center text-sm text-gray-500">
              <p>${moneyString(item.price)}</p>
              <p className="mx-4">Rating: {getItemRating(item)}</p>
              <p>Orders: {orderCountMap[item.id] || 0}</p>
            </div>
            {hasMenuItemMods(item) && (
              <>
                <button
                  className="cursor-pointer font-semibold text-left mt-2"
                  onClick={() =>
                    setShowItemMods((prev) => ({
                      ...prev,
                      [item.id]: !prev[item.id],
                    }))
                  }
                >
                  {showItemMods[item.id] ? 'Hide Mods' : 'Show Mods'}
                </button>
                {Boolean(showItemMods[item.id]) && (
                  <div className="flex flex-col mt-2">
                    {getSortedModGroups(item.availableModGroups).map(
                      renderModGroup,
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex flex-row items-center">
            {hasAnyMenuItemMods(items) && (
              <TooltipModal
                unicode="&#9888;"
                width="400px"
                information='*Totals shown below are for base menu items only. To see user modifications, view individual orders in the "Orders" tab.'
                marginTop="20px"
                color="red"
              />
            )}
            {groupOrder && !isCancelled && isBeforeCutoff(menuCutoffTime) && (
              <button
                className="flex flex-row items-center text-red-500 ml-4"
                onClick={() => onDeleteMenuItem(item, menu)}
              >
                <FaTrashAlt size={16} className="mr-2" color="red" />
                Delete Item
              </button>
            )}
          </div>
        </div>
      )
    }

    return (
      <div className="flex flex-col mb-2">
        <p className="text-lg font-bold">{label}</p>
        <div className="flex flex-col mt-2">{items.map(renderMenuItem)}</div>
      </div>
    )
  }

  const renderMenuItems = (
    menu: VendorMenuGroup['menus'][number],
    isMenuCancelled: boolean,
  ) => {
    const { entrees, sides, drinks, desserts } = menu

    return (
      <>
        {renderItemCategory(
          'Entrees',
          menu,
          entrees,
          menu.cutoffTime,
          isMenuCancelled,
        )}
        {renderItemCategory(
          'Sides',
          menu,
          sides,
          menu.cutoffTime,
          isMenuCancelled,
        )}
        {renderItemCategory(
          'Drinks',
          menu,
          drinks,
          menu.cutoffTime,
          isMenuCancelled,
        )}
        {renderItemCategory(
          'Desserts',
          menu,
          desserts,
          menu.cutoffTime,
          isMenuCancelled,
        )}
      </>
    )
  }

  const renderMenu = (menu: VendorMenuGroup['menus'][number]) => {
    const { entrees, sides, drinks, desserts } = menu
    const isCancelled =
      menu.cancellationEventId !== null ||
      [...entrees, ...sides, ...drinks, ...desserts].every(
        (item) => item.cancellationEventId !== null,
      )

    const backgroundColor = isCancelled ? 'bg-red-100' : 'bg-gray-100'
    const hoverBackgroundColor = isCancelled
      ? 'hover:bg-red-300'
      : 'hover:bg-gray-300'
    const borderColor = isCancelled ? '#f56565' : '#a0aec0'

    return (
      <div key={menu.id}>
        <div
          className={`flex flex-row justify-between items-center ${backgroundColor} ${hoverBackgroundColor} cursor-pointer p-2`}
          style={{ borderBottom: `1px solid ${borderColor}` }}
          onClick={() =>
            setExpandedMenuIds((prev) => ({
              ...prev,
              [menu.id]: !prev[menu.id],
            }))
          }
        >
          <div className="flex flex-col" style={{ width: '65%' }}>
            <div className="flex flex-row items-center justify-between">
              <p className="text-lg font-bold mr-3">
                {menu.menuName} -{' '}
                {isBeforeCutoff(menu.cutoffTime)
                  ? 'Open for Orders'
                  : 'LOCKED (Cutoff Passed)'}
              </p>
            </div>
            <p className="text-sm text-gray-500">
              Order Count: {menu.orderCount} • Cutoff Time:{' '}
              {Moment(menu.cutoffTime).format('lll z')}
              {updatedCutoffMap[menu.id] && (
                <span className="text-orange-700 font-bold">
                  {` • Cutoff Updated`}
                </span>
              )}
            </p>
            {menu.cutoffEmailSentAt && (
              <p className="text-sm text-gray-500">
                Cutoff Email Sent At:{' '}
                {Moment(menu.cutoffEmailSentAt).format('lll z')}
              </p>
            )}
            <div className="flex flex-row items-center mt-2">
              {Moment().isAfter(Moment(menu.cutoffTime)) ? (
                <p
                  className="capitalize text-red-500 bg-red-200 rounded-md p-2 font-semibold mr-3"
                  style={{
                    border: '1px solid #f56565',
                    maxWidth: 'fit-content',
                  }}
                >
                  After Cutoff
                </p>
              ) : (
                <p
                  className="capitalize text-green-500 bg-green-200 rounded-md p-2 font-semibold mr-3"
                  style={{
                    border: '1px solid #48bb78',
                    maxWidth: 'fit-content',
                  }}
                >
                  Before Cutoff
                </p>
              )}
              {Moment(menu.cutoffTime).isAfter(Moment()) && (
                <FlexContainer
                  flexDirection="row"
                  justifyContent="center"
                  width="35%"
                >
                  <AuthorizedDisplay roles={['master admin', 'chef lead']}>
                    <button
                      className="flex flex-row items-center bg-blue-500 text-white px-2 py-1 rounded-md"
                      onClick={() => onEditMenuCutoffTime(menu.id)}
                    >
                      <BiPencil />
                      Edit Cutoff Time
                    </button>
                  </AuthorizedDisplay>
                  <TooltipModal
                    unicode="&#9432;"
                    width="400px"
                    information={
                      'Primary use of this edit button should to be fix an incorrectly entered menu card cutoff time.'
                    }
                    marginTop="20px"
                  />
                </FlexContainer>
              )}
            </div>
          </div>
          <div className="flex flex-row items-center">
            <div
              className="font-semibold rounded-md p-2 mr-5"
              style={{
                maxWidth: 'fit-content',
                backgroundColor: menu.isOverBudget ? '#ecc94b' : '#68d391',
                color: menu.isOverBudget ? '#000' : '#fff',
              }}
            >
              ${menu.budgetPerPerson.toFixed(2)}
            </div>
            {expandedMenuIds[menu.id] ? (
              <FaChevronUp size={16} />
            ) : (
              <FaChevronDown size={16} />
            )}
          </div>
        </div>
        {Boolean(expandedMenuIds[menu.id]) && (
          <div
            className="p-2 bg-white w-full"
            style={{ borderBottom: '1px solid #a0aec0' }}
          >
            <div className="flex flex-row justify-between items-center w-full">
              <Checkbox
                label={`Active?${
                  menu.lastActive
                    ? ` (Last Activated: ${Moment(menu.lastActive).format(
                        'MM-DD-YYYY h:mm a',
                      )})`
                    : ''
                }`}
                checkedColor="#4299e1"
                labelStyle="text-sm text-gray-600"
                checked={menu.active}
                onChange={onChangeMenuActive(menu.id)}
              />
              <div className="flex flex-row items-center w-1/2 justify-end">
                <button
                  className="p-2 text-white bg-blue-400 rounded-md mr-2"
                  style={{ width: 'fit-content' }}
                  onClick={async () => {
                    const menuToSync = dateMenu?.menus?.find(
                      (m: Menu) => m.id === menu.id,
                    )
                    if (!menuToSync) {
                      return
                    }
                    await syncMenu(menuToSync)
                  }}
                >
                  Sync Menu
                </button>
                {!isCancelled && isBeforeCutoff(menu.cutoffTime) && (
                  <button
                    className="p-2 text-white bg-red-500 rounded-md flex flex-row items-center"
                    style={{ width: 'fit-content' }}
                    onClick={() => onDeleteMenu(menu, vendorMenus)}
                  >
                    <FaTrash size={16} className="mr-2" color="white" />
                    Delete Menu
                  </button>
                )}
              </div>
            </div>
            {renderMenuItems(menu, isCancelled)}
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className="flex flex-col rounded-md shadow-md bg-gray-200 mb-2"
      style={{ border: '2px solid #a0aec0' }}
    >
      <div
        className="flex flex-row justify-between items-center p-2"
        style={{ borderBottom: '2px solid #a0aec0' }}
      >
        <div>
          <p className="text-lg font-bold">{vendorMenus.vendorName}</p>
          <p className="text-sm text-gray-500">
            {vendorMenus.menuCount} menus • {vendorMenus.totalOrderCount} total
            orders
          </p>
        </div>
        {!vendorMenus.cancelled &&
          isBeforeCutoff(Moment(earliestMenuCutoffTime).format()) && (
            <button
              className="flex flex-row items-center bg-red-500 text-white px-2 py-1 rounded-md"
              onClick={() => onDeleteVendorMenus(vendorMenus)}
            >
              <FaTrash size={16} className="mr-2" color="white" />
              Delete All Menus
            </button>
          )}
      </div>
      {vendorMenus.menus.map(renderMenu)}
    </div>
  )
}
